import React from 'react';
import { Table, Tag, Button, Space, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import {
  EyeOutlined,
  CopyOutlined,
  LinkOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { TransactionLogSummary } from '../../../services/transactionLogsService';
import { formatVnDateTime } from '../../../utils/vnDateTime';
import { formatInternalStatusLabel } from '../../../utils/transactionLogStatusLabels';

const formatVnd = (v: number | null | undefined): string => {
  if (v == null) return '–';
  return (
    new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v) + ' đ'
  );
};

const formatDateTime = (v: string | null | undefined): string => {
  if (!v) return '–';
  const formatted = formatVnDateTime(v);
  return formatted === '—' ? '–' : formatted;
};

const STATUS_COLORS: Record<string, string> = {
  SUCCEEDED: 'green',
  CAPTURED: 'green',
  COMPLETED: 'green',
  PENDING: 'gold',
  PENDING_APPROVAL: 'gold',
  FAILED: 'red',
  CANCELLED: 'default',
  EXPIRED: 'default',
};

const REFUND_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Chờ hoàn tiền',
  SUCCEEDED: 'Đã hoàn tiền',
  FAILED: 'Hoàn tiền thất bại',
};

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard.writeText(text).then(
    () => message.success(`Đã copy ${label}`),
    () => message.error('Không thể copy')
  );
};

interface TransactionLogsTableProps {
  data: TransactionLogSummary[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize?: number) => void;
  onViewDetail: (record: TransactionLogSummary) => void;
}

export const TransactionLogsTable: React.FC<TransactionLogsTableProps> = ({
  data,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onViewDetail,
}) => {
  const columns: ColumnsType<TransactionLogSummary> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => (
        <Space size="small">
          <span>{id}</span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={e => {
              e.stopPropagation();
              copyToClipboard(String(id), 'Transaction ID');
            }}
          />
        </Space>
      ),
    },
    {
      title: 'Order',
      key: 'order',
      width: 140,
      render: (_: unknown, r: TransactionLogSummary) => (
        <div>
          {r.order_id ? (
            <Link
              to={`/orders/${r.order_id}`}
              onClick={e => e.stopPropagation()}
            >
              {r.order_code ? `#${r.order_code}` : '–'}
            </Link>
          ) : (
            '–'
          )}
        </div>
      ),
    },
    {
      title: 'Merchant',
      dataIndex: 'merchant_name',
      key: 'merchant',
      width: 140,
      render: (name: string | null, r: TransactionLogSummary) =>
        r.restaurant_id ? (
          <Link
            to={`/merchants/${r.restaurant_id}`}
            onClick={e => e.stopPropagation()}
          >
            {name || `#${r.restaurant_id}`}
          </Link>
        ) : (
          name || '–'
        ),
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 140,
      render: (_: unknown, r: TransactionLogSummary) => (
        <span>{r.customer_name || r.customer_phone || '–'}</span>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      width: 90,
      render: (v: string) => (
        <Tag color={v === 'refund' ? 'orange' : 'blue'}>
          {v === 'refund' ? 'Hoàn tiền' : 'Thanh toán'}
        </Tag>
      ),
    },
    {
      title: 'Phương thức',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 110,
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (v: number | null) => formatVnd(v),
    },
    {
      title: 'Trạng thái hoàn tiền',
      key: 'refund_status',
      width: 130,
      render: (_: unknown, r: TransactionLogSummary) => {
        if (r.transaction_type === 'refund') {
          const s = r.internal_status;
          if (s === 'SUCCEEDED') return 'Đã hoàn tiền';
          if (s === 'PENDING') return 'Đang xử lý';
          if (s === 'FAILED') return 'Hoàn tiền thất bại';
          return s || '–';
        }
        const orderCancelled =
          r.order_status &&
          String(r.order_status).toLowerCase() === 'cancelled';
        const refundStatus = r.order_refund_status
          ? String(r.order_refund_status).toUpperCase()
          : null;
        if (!orderCancelled || !refundStatus) return '–';
        if (refundStatus === 'SUCCEEDED') return 'Đã hoàn tiền';
        if (refundStatus === 'PENDING') return 'Đang xử lý';
        if (refundStatus === 'FAILED') return 'Hoàn tiền thất bại';
        return '–';
      },
    },
    {
      title: 'Merchant amount',
      dataIndex: 'merchant_amount',
      key: 'merchant_amount',
      width: 120,
      align: 'right',
      render: (v: number | null) => formatVnd(v),
    },
    {
      title: 'Trạng thái nội bộ',
      dataIndex: 'internal_status',
      key: 'internal_status',
      width: 140,
      render: (v: string | null, r: TransactionLogSummary) => {
        const label = formatInternalStatusLabel(v, r.transaction_type);
        return <Tag color={STATUS_COLORS[v || ''] || 'default'}>{label}</Tag>;
      },
    },
    // {
    //   title: 'Gateway',
    //   dataIndex: 'gateway_status',
    //   key: 'gateway_status',
    //   width: 110,
    //   render: (v: string | null) => (
    //     <Tag color={STATUS_COLORS[v || ''] || 'default'}>{v || '–'}</Tag>
    //   ),
    // },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 80,
    },
    {
      title: 'Reference',
      dataIndex: 'reference_id',
      key: 'reference_id',
      width: 100,
      render: (v: string | null, r: TransactionLogSummary) => (
        <Space size="small">
          <span
            style={{
              maxWidth: 80,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {v || '–'}
          </span>
          {v && (
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={e => {
                e.stopPropagation();
                copyToClipboard(v, 'Reference ID');
              }}
            />
          )}
        </Space>
      ),
    },
    {
      title: 'Webhook',
      dataIndex: 'webhook_received_at',
      key: 'webhook',
      width: 90,
      render: (v: string | null) => (v ? '✓' : '–'),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_: unknown, r: TransactionLogSummary) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewDetail(r)}
          >
            Chi tiết
          </Button>
          {r.order_id && (
            <Tooltip title="Mở đơn hàng">
              <Link to={`/orders/${r.order_id}`}>
                <Button type="link" size="small" icon={<LinkOutlined />} />
              </Link>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table<TransactionLogSummary>
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (t, range) => `${range[0]}-${range[1]} / ${t}`,
      }}
      onChange={pagination => {
        const current = pagination?.current ?? page;
        const size = pagination?.pageSize ?? pageSize;
        onPageChange(current, size);
      }}
      onRow={record => ({
        onClick: () => onViewDetail(record),
        style: { cursor: 'pointer' },
      })}
      scroll={{ x: 1800 }}
    />
  );
};
